import { formatDisplayUnits } from "../helpers/amountDisplay";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { styles } from "../../styles";
import { getTokenByAddress } from "../../config";
import type { BaseLogEntry } from "../types";
import type { JoinSplitNote, StoredJoinSplitNote } from "../../joinsplit/types";
import {
  archiveInboxCandidate,
  capacitySeverity,
  createRecoveryFile,
  fetchInboxPage,
  fetchRecoveryStatus,
  openRecoveryNote,
  RECOVERY_FILE_MAX_BYTES,
  registerRecovery,
  restoreRecoveryFile,
  type InboxLane,
  type RecoveryContext,
  type RecoveryEnvelope,
  type RecoveryStatus,
} from "../../joinsplit-v2/recoveryManagement";
import {
  listSavedFlatSettlementRetries,
  type SavedFlatSettlementRetry,
} from "../../joinsplit-v2/settlementRetry";

interface Props {
  context: RecoveryContext | null;
  session: object;
  notes: readonly StoredJoinSplitNote[];
  logs: BaseLogEntry[];
  onRestoreNotes: (
    candidates: { enc: RecoveryEnvelope; note: JoinSplitNote }[],
    isCurrent: () => boolean,
    signal?: AbortSignal,
  ) => Promise<number>;
  onReconcile: () => void | Promise<void>;
  onRetrySettlement?: (
    record: SavedFlatSettlementRetry,
    isCurrent: () => boolean,
    signal?: AbortSignal,
  ) => Promise<void>;
}
const button: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #435374",
  background: "#182139",
  color: "#e8eeff",
  cursor: "pointer",
};
const bytes = (value: number) => `${(value / 1048576).toFixed(2)} MiB`;
const short = (value: string) => `${value.slice(0, 12)}…${value.slice(-6)}`;
const retryLabels = {
  joinsplit: "private transfer",
  withdraw: "withdrawal",
  withdraw_batch: "batch withdrawal",
  migrate: "note migration",
} as const;
const formatRetryAmount = (amount: string, tokenAddress: string) => {
  const token = getTokenByAddress(tokenAddress);
  return token
    ? `${formatDisplayUnits(amount, token.decimals)} ${token.symbol}`
    : `${amount} base units`;
};

/** Owner actions only. No automatic archival, deletion, or backend-derived spendability. */
export function BaseRecoveryPanel({
  context,
  session,
  notes,
  logs,
  onRestoreNotes,
  onReconcile,
  onRetrySettlement,
}: Props) {
  const [status, setStatus] = useState<RecoveryStatus | null>(null);
  const [lane, setLane] = useState<InboxLane>("pending");
  const [page, setPage] = useState<RecoveryEnvelope[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [inspection, setInspection] = useState<Record<string, string>>({});
  const [savedTransactions, setSavedTransactions] = useState<
    SavedFlatSettlementRetry[]
  >([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const active = useRef(session);
  const activeContext = useRef(context);
  const serial = useRef(0);
  const loadingRef = useRef(false);
  const requestController = useRef<AbortController | null>(null);
  if (active.current !== session || activeContext.current !== context) {
    active.current = session;
    activeContext.current = context;
    serial.current++;
    loadingRef.current = false;
    requestController.current?.abort();
  }
  useEffect(() => {
    setStatus(null);
    setPage([]);
    setNextCursor(null);
    setError("");
    setMessage("");
    setInspection({});
    setSavedTransactions([]);
    setBusy(false);
    return () => {
      if (active.current === session && activeContext.current === context) {
        serial.current++;
        loadingRef.current = false;
        requestController.current?.abort();
      }
    };
  }, [session, context]);
  const run = useCallback(
    async (
      fn: (ctx: RecoveryContext, isCurrent: () => boolean) => Promise<void>,
    ) => {
      if (!context || loadingRef.current) return;
      const id = ++serial.current;
      const isCurrent = () =>
        active.current === session &&
        serial.current === id &&
        context.isCurrent();
      const controller = new AbortController();
      requestController.current = controller;
      const ctx = { ...context, isCurrent, signal: controller.signal };
      loadingRef.current = true;
      setBusy(true);
      setError("");
      setMessage("");
      try {
        await fn(ctx, isCurrent);
      } catch (err) {
        if (isCurrent())
          setError(
            err instanceof Error
              ? err.message
              : "Recovery operation failed; keep the original backup file.",
          );
      } finally {
        if (isCurrent()) {
          loadingRef.current = false;
          setBusy(false);
        }
      }
    },
    [context, session],
  );
  const refresh = useCallback(
    () =>
      run(async (ctx, valid) => {
        const [next, saved] = await Promise.all([
          fetchRecoveryStatus(ctx),
          onRetrySettlement
            ? listSavedFlatSettlementRetries(ctx.scope)
            : Promise.resolve([]),
        ]);
        if (valid()) {
          setStatus((old) => (valid() ? next : old));
          setSavedTransactions((old) => (valid() ? saved : old));
        }
      }),
    [run, onRetrySettlement],
  );
  useEffect(() => {
    if (context) void refresh();
  }, [context, refresh]);
  const loadPage = (selected: InboxLane, cursor?: string) =>
    run(async (ctx, valid) => {
      const result = await fetchInboxPage(ctx, selected, cursor);
      if (!valid()) return;
      setLane(selected);
      setPage((old) => (valid() ? result.notes : old));
      setNextCursor(result.nextCursor);
      setInspection({});
    });
  const download = () =>
    run(async (ctx, valid) => {
      const raw = await createRecoveryFile(ctx, notes);
      if (!valid()) return;
      const url = URL.createObjectURL(
        new Blob([raw], { type: "application/json" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `stealthos-recovery-${ctx.scope.chainId}-${Date.now()}.encrypted.json`;
      link.click();
      URL.revokeObjectURL(url);
      if (valid())
        setMessage(
          "Encrypted file downloaded. Keep it off this device with your original wallet recovery credentials. No private keys are in the file.",
        );
    });
  const importFile = (file?: File) => {
    if (!file) return;
    void run(async (ctx, valid) => {
      if (file.size > RECOVERY_FILE_MAX_BYTES)
        throw new Error("Recovery file exceeds 16 MiB limit");
      const raw = await file.text();
      if (!valid()) return;
      let result;
      try {
        result = await restoreRecoveryFile(ctx, raw, onRestoreNotes);
      } catch (error) {
        throw new Error(
          `${error instanceof Error ? error.message : "Recovery stopped"}. Keep the original file and retry; any completed imports remain stored.`,
        );
      }
      if (!valid()) return;
      setMessage(
        `Restored ${result.journals} missing journal records and ${result.notes} chain-validated notes; ${result.skipped} unauthenticated inbox candidates were not imported. Existing notes were retained. Keep the file until recovery is verified.`,
      );
      await onReconcile();
    });
  };
  const inspect = (enc: RecoveryEnvelope) =>
    run(async (ctx, valid) => {
      let description: string;
      try {
        const note = await openRecoveryNote(ctx, enc);
        description = `Authenticated as yours; raw value ${note.value.toString()}, token ${note.tokenData?.tokenAddress || "unknown"}. Chain inclusion and spent state still require checking.`;
      } catch {
        description =
          "Not authenticated as a valid note for this privacy key. This may be junk or incompatible ciphertext; no data was deleted.";
      }
      if (valid())
        setInspection((old) =>
          valid() ? { ...old, [enc.deliveryId!]: description } : old,
        );
    });
  const recoveryLogs = logs
    .filter((entry) =>
      /recovery|journal|inbox|capacity|backup|delivery|sync|validation|refresh/i.test(
        entry.message,
      ),
    )
    .slice(-10);
  return (
    <section
      style={{ ...styles.card, marginTop: 20 }}
      aria-label="Recovery and inbox"
    >
      <h2 style={{ color: "#e8eeff", marginTop: 0 }}>Recovery &amp; inbox</h2>
      <p style={{ color: "#aebfdc" }}>
        Encrypted backups require the original wallet and privacy credentials,
        on the same chain and pool. Archiving hides pending mail; it never
        deletes ciphertext or frees global retained-storage capacity.
      </p>
      {!context && (
        <p role="status">
          Connect and sign in to manage your encrypted recovery data.
        </p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <button
          style={button}
          disabled={!context || busy}
          onClick={() => void refresh()}
        >
          Refresh status
        </button>
        <button
          style={button}
          disabled={!context || busy}
          onClick={() =>
            void run(async (ctx, valid) => {
              await registerRecovery(ctx);
              const next = await fetchRecoveryStatus(ctx);
              if (valid()) {
                setStatus(next);
                setMessage(
                  "Recovery registration acknowledged. Readiness below refers to public-chain indexing, not a guarantee that every private note was backed up.",
                );
              }
            })
          }
        >
          Register recovery
        </button>
        <button
          style={button}
          disabled={!context || busy}
          onClick={() => void download()}
        >
          Download encrypted backup
        </button>
        <button
          style={button}
          disabled={!context || busy}
          onClick={() => fileRef.current?.click()}
        >
          Restore encrypted file
        </button>
        <input
          ref={fileRef}
          hidden
          type="file"
          accept="application/json,.json"
          aria-label="Encrypted recovery file"
          onChange={(event) => {
            importFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </div>
      {busy && <p role="status">Working on encrypted recovery data…</p>}
      {error && (
        <p role="alert" style={{ color: "#ffb7a9" }}>
          {error}
        </p>
      )}
      {message && (
        <p role="status" style={{ color: "#b6e3c6" }}>
          {message}
        </p>
      )}
      {status && (
        <>
          <p role="status">
            Public-chain recovery index:{" "}
            {status.ingestion.ready
              ? "ready"
              : status.ingestion.configured
                ? "not ready / catching up"
                : "not configured"}
            . This is not a promise of private-note availability or off-host
            backup.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "left" }}>
              <thead>
                <tr>
                  <th>Storage</th>
                  <th>Records</th>
                  <th>Encrypted bytes</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(["pending", "protected", "archive", "journal"] as const).map(
                  (name) => {
                    const cap = status.capacity[name];
                    const level = capacitySeverity(cap);
                    return (
                      <tr key={name}>
                        <td>{name}</td>
                        <td>
                          {cap.records} / {cap.recordLimit}
                        </td>
                        <td>
                          {bytes(cap.bytes)} / {bytes(cap.byteLimit)}
                        </td>
                        <td
                          style={{
                            color: level === "ok" ? "#b6e3c6" : "#ffd18e",
                          }}
                        >
                          {level === "ok"
                            ? "Available"
                            : level === "error"
                              ? "Full — contact operator"
                              : "Near capacity — export and contact operator"}
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>
          {Object.values(status.capacity).some(
            (cap) => capacitySeverity(cap) !== "ok",
          ) && (
            <p role="alert" style={{ color: "#ffd18e" }}>
              Recovery storage is nearing capacity or full. Download an
              encrypted copy and request more capacity. Do not discard
              potentially valid notes; new submissions may be blocked safely.
            </p>
          )}
          {status.alerts.map((alert, index) => (
            <p key={index} role="alert" style={{ color: "#ffd18e" }}>
              {alert.message}
            </p>
          ))}
        </>
      )}
      {onRetrySettlement && savedTransactions.length > 0 && (
        <section aria-label="Saved transactions awaiting settlement">
          <h3>Retry a saved transaction</h3>
          <p>
            Review the original destinations and amounts before retrying. This
            sends the same transaction through the relayer. No new deposit or
            wallet gas payment is requested.
          </p>
          {savedTransactions.map((record) => (
            <article
              key={record.id}
              style={{ padding: "12px 0", borderBottom: "1px solid #344058" }}
            >
              <h4>Saved {retryLabels[record.kind]}</h4>
              <p>
                Token: <code>{record.token}</code>
              </p>
              <ul>
                {record.payouts.map((payout, index) => (
                  <li key={`${payout.recipient}:${index}`}>
                    {formatRetryAmount(payout.amount, record.token)} to{" "}
                    {payout.private ? (
                      `${payout.keepLocal ? "your private wallet" : "private recipient"} (${short(payout.recipient)})`
                    ) : (
                      <code>{payout.recipient}</code>
                    )}
                  </li>
                ))}
              </ul>
              <p>Saved fee: {formatRetryAmount(record.fee, record.token)}</p>
              <button
                style={button}
                disabled={!context || busy}
                onClick={() =>
                  void run(async (ctx, valid) => {
                    await onRetrySettlement(record, valid, ctx.signal);
                    if (!valid()) return;
                    await onReconcile();
                    if (!valid()) return;
                    const saved = await listSavedFlatSettlementRetries(
                      ctx.scope,
                    );
                    if (!valid()) return;
                    setSavedTransactions((old) => (valid() ? saved : old));
                    setMessage(
                      "Saved transaction settled. Recovery and balance were refreshed.",
                    );
                  })
                }
              >
                Retry saved {retryLabels[record.kind]}
              </button>
            </article>
          ))}
        </section>
      )}
      <h3>Inspect encrypted deliveries</h3>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(["pending", "protected", "archive"] as const).map((name) => (
          <button
            key={name}
            style={button}
            disabled={!context || busy}
            onClick={() => void loadPage(name)}
          >
            {name}
          </button>
        ))}
        <button
          style={button}
          disabled={!context || busy || !nextCursor}
          onClick={() => void loadPage(lane, nextCursor!)}
        >
          Next page
        </button>
      </div>
      <p>
        {lane}: {page.length} deliveries on this page. Public labels are
        untrusted until decrypted and checked.
      </p>
      {page.map((enc) => (
        <div
          key={enc.deliveryId}
          style={{ padding: "12px 0", borderBottom: "1px solid #344058" }}
        >
          <code>{short(enc.deliveryId!)}</code>
          <span> · {enc.storageClass}</span>
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}
          >
            <button
              style={button}
              disabled={busy}
              onClick={() => void inspect(enc)}
            >
              Inspect locally
            </button>
            <button
              style={button}
              disabled={busy}
              onClick={() =>
                void run(async (ctx, valid) => {
                  const note = await openRecoveryNote(ctx, enc);
                  const count = await onRestoreNotes(
                    [{ enc, note }],
                    valid,
                    ctx.signal,
                  );
                  if (valid())
                    setMessage(
                      `${count} chain-validated note(s) recovered. Pending, invalid or spent records are not made spendable.`,
                    );
                })
              }
            >
              Check on-chain &amp; recover
            </button>
            {enc.storageClass === "pending" && (
              <button
                style={button}
                disabled={busy}
                onClick={() => {
                  if (
                    !window.confirm(
                      "Archive this pending delivery? It may contain a valid payment. It will be hidden from normal inbox sync but retained in the archive; no ciphertext is deleted.",
                    )
                  )
                    return;
                  void run(async (ctx, valid) => {
                    await archiveInboxCandidate(ctx, enc);
                    if (valid()) {
                      setPage((old) =>
                        valid()
                          ? old.filter(
                              (item) => item.deliveryId !== enc.deliveryId,
                            )
                          : old,
                      );
                      setMessage(
                        "Delivery archived and retained. Use the archive tab to inspect or recover it.",
                      );
                    }
                  });
                }}
              >
                Archive pending delivery
              </button>
            )}
          </div>
          {inspection[enc.deliveryId!] && <p>{inspection[enc.deliveryId!]}</p>}
        </div>
      ))}
      <details
        style={{ marginTop: 16 }}
        open={recoveryLogs.some(
          (entry) => entry.type === "warning" || entry.type === "error",
        )}
      >
        <summary>Recent recovery activity</summary>
        {recoveryLogs.length === 0 ? (
          <p>No recovery activity in this session.</p>
        ) : (
          recoveryLogs.map((entry) => (
            <p
              key={entry.id}
              role={
                entry.type === "warning" || entry.type === "error"
                  ? "alert"
                  : "status"
              }
            >
              {entry.timestamp.toLocaleTimeString()} · {entry.message}
            </p>
          ))
        )}
      </details>
    </section>
  );
}
